<template>
  <!-- Asks before a change that affects people: what will happen, in a short
       list when there's one, then Cancel or the change itself. -->
  <div
    ref="dialog"
    class="modal dn-modal show"
    tabindex="-1"
    role="alertdialog"
    aria-modal="true"
    :aria-labelledby="titleId"
    :aria-describedby="effects.length ? listId : null"
    @keydown.esc.stop="close"
  >
    <div class="modal-dialog dn-modal-dialog dn-modal-dialog--sm">
      <div class="modal-content dn-modal-content">
        <div class="dn-modal-header" :class="{ 'cm-header--alone': !hasBody }">
          <div class="dn-modal-heading">
            <span class="dn-modal-glyph" :class="glyphClass" aria-hidden="true">
              <img v-if="image" :src="image" alt="" class="cm-glyph-image" />
              <i v-else class="pi" :class="icon"></i>
            </span>
            <div class="dn-modal-heading-text">
              <h5 :id="titleId" class="dn-modal-title">{{ title }}</h5>
              <p v-if="subtitle" class="dn-modal-subtitle">{{ subtitle }}</p>
            </div>
          </div>
          <button type="button" class="dn-modal-close" aria-label="Close" :disabled="busy" @click="close">
            <i class="pi pi-times"></i>
          </button>
        </div>

        <div v-if="hasBody" class="dn-modal-body">
          <ul v-if="effects.length" :id="listId" class="cm-effects">
            <li v-for="(effect, index) in effects" :key="index">
              <i class="pi" :class="effect.icon || 'pi-info-circle'" aria-hidden="true"></i>
              <span>{{ effect.text }}</span>
            </li>
          </ul>
          <slot />
        </div>

        <div class="dn-modal-footer">
          <button type="button" class="btn btn-custom-secondary" :disabled="busy" @click="close">
            {{ cancelLabel }}
          </button>
          <button
            ref="confirm"
            type="button"
            class="btn btn-custom-primary"
            :class="{ 'dn-btn-danger': tone === 'danger' }"
            :disabled="busy"
            @click="$emit('confirm')"
          >
            <span v-if="busy" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span v-else>{{ confirmLabel }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
let uid = 0;

export default {
  name: 'ConfirmModal',
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    // primeicons class for the glyph, or an image (a brand's mark) instead.
    icon: { type: String, default: 'pi-question-circle' },
    image: { type: String, default: '' },
    // primary, or danger for something that takes things away.
    tone: { type: String, default: 'primary' },
    // [{ icon, text }] — what will happen.
    effects: { type: Array, default: () => [] },
    confirmLabel: { type: String, default: 'Confirm' },
    cancelLabel: { type: String, default: 'Cancel' },
    busy: { type: Boolean, default: false },
  },
  emits: ['confirm', 'close'],
  data() {
    uid += 1;
    return {
      titleId: `confirm-modal-title-${uid}`,
      listId: `confirm-modal-effects-${uid}`,
    };
  },
  computed: {
    // Nothing to list: the title says it all, so there's no empty body under it.
    hasBody() {
      return this.effects.length > 0 || Boolean(this.$slots.default);
    },
    glyphClass() {
      if (this.image) return 'cm-glyph--image';
      return this.tone === 'danger' ? 'dn-modal-glyph--danger' : '';
    },
  },
  methods: {
    close() {
      if (!this.busy) this.$emit('close');
    },
  },
  mounted() {
    // Focus inside the dialog, so Escape and Tab work straight away.
    this.$refs.dialog?.focus();
  },
};
</script>
<style scoped>
/* Without a body, the header keeps the title off the footer's divider. */
.cm-header--alone {
  padding-bottom: 1rem;
}

.cm-effects {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.cm-effects li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  line-height: 1.5;
}

.cm-effects .pi {
  flex: none;
  margin-top: 3px;
  color: var(--color-text-muted);
  font-size: 0.85rem;
}

.cm-glyph--image {
  background: var(--color-bg-canvas) !important;
  border: 1px solid var(--color-border);
}

.cm-glyph-image {
  width: auto;
  height: 22px;
}
</style>
